import { Suspense, lazy, useEffect, useState } from 'react';
import { Route, Routes } from 'react-router-dom';

import { RequireAuth } from '@/components/RequireAuth';
import {
  RequireAdminRole,
  RequirePlatformRole,
} from '@/components/RequireAdminRole';
import { RootRedirect } from '@/components/RootRedirect';
import { useAuthStore } from '@/stores/useAuthStore';

import { LoginPage } from '@/pages/LoginPage';
import { ForgotPasswordPage } from '@/pages/ForgotPasswordPage';
import { ResetPasswordPage } from '@/pages/ResetPasswordPage';
import { NoAccessPage } from '@/pages/NoAccessPage';
import { MfaChallengePage } from '@/pages/MfaChallengePage';
import { NotFoundPage } from '@/pages/NotFoundPage';

const OrgTreePage = lazy(() =>
  import('@/pages/OrgTreePage').then((m) => ({ default: m.OrgTreePage })),
);
const OrgDetailPage = lazy(() =>
  import('@/pages/OrgDetailPage').then((m) => ({ default: m.OrgDetailPage })),
);
const SitesPage = lazy(() =>
  import('@/pages/SitesPage').then((m) => ({ default: m.SitesPage })),
);
const SiteDetailPage = lazy(() =>
  import('@/pages/SiteDetailPage').then((m) => ({ default: m.SiteDetailPage })),
);
const ContractsPage = lazy(() =>
  import('@/pages/ContractsPage').then((m) => ({ default: m.ContractsPage })),
);
const DelegationsPage = lazy(() =>
  import('@/pages/DelegationsPage').then((m) => ({
    default: m.DelegationsPage,
  })),
);
const ApprovalsPage = lazy(() =>
  import('@/pages/ApprovalsPage').then((m) => ({ default: m.ApprovalsPage })),
);
const UsersPage = lazy(() =>
  import('@/pages/UsersPage').then((m) => ({ default: m.UsersPage })),
);
const ApiKeysPage = lazy(() =>
  import('@/pages/ApiKeysPage').then((m) => ({ default: m.ApiKeysPage })),
);
const ExternalCredentialsPage = lazy(() =>
  import('@/pages/ExternalCredentialsPage').then((m) => ({
    default: m.ExternalCredentialsPage,
  })),
);
const OpsDashboardPage = lazy(() =>
  import('@/pages/OpsDashboardPage').then((m) => ({
    default: m.OpsDashboardPage,
  })),
);
const OpsAuditPage = lazy(() =>
  import('@/pages/OpsAuditPage').then((m) => ({ default: m.OpsAuditPage })),
);
const OpsOrganizationsPage = lazy(() =>
  import('@/pages/OpsOrganizationsPage').then((m) => ({
    default: m.OpsOrganizationsPage,
  })),
);
const ProfilePage = lazy(() =>
  import('@/pages/ProfilePage').then((m) => ({ default: m.ProfilePage })),
);
const MfaSetupPage = lazy(() =>
  import('@/pages/MfaSetupPage').then((m) => ({ default: m.MfaSetupPage })),
);

const protect = (children: React.ReactNode) => (
  <RequireAuth>
    <RequireAdminRole>{children}</RequireAdminRole>
  </RequireAuth>
);

const opsOnly = (children: React.ReactNode) => (
  <RequireAuth>
    <RequirePlatformRole>{children}</RequirePlatformRole>
  </RequireAuth>
);

function RouteFallback() {
  return (
    <div className="flex min-h-screen items-center justify-center text-sm text-muted-foreground">
      불러오는 중...
    </div>
  );
}

export function App() {
  const bootstrap = useAuthStore((s) => s.bootstrap);
  const loading = useAuthStore((s) => s.loading);
  // A cold tab must restore its session before protected routes can redirect.
  // Persisted users retain the existing immediate render/background refresh flow.
  const [bootstrapped, setBootstrapped] = useState(() => Boolean(useAuthStore.getState().user));

  useEffect(() => {
    let mounted = true;
    void bootstrap().finally(() => {
      if (mounted) setBootstrapped(true);
    });
    return () => { mounted = false; };
  }, [bootstrap]);

  if (!bootstrapped || loading) {
    return (
      <div className="flex min-h-screen items-center justify-center text-muted-foreground">
        로딩 중...
      </div>
    );
  }

  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        {/* 공개 */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/no-access" element={<NoAccessPage />} />

        {/* MFA — login challenge 는 인증 전, setup 은 mfa_pending 토큰으로 접근 */}
        <Route path="/mfa/challenge" element={<MfaChallengePage />} />
        <Route
          path="/mfa/setup"
          element={
            <RequireAuth>
              <MfaSetupPage />
            </RequireAuth>
          }
        />

        {/* 인증 + admin 권한 필요 */}
        <Route
          path="/"
          element={
            <RequireAuth>
              <RootRedirect />
            </RequireAuth>
          }
        />
        <Route path="/org-tree" element={protect(<OrgTreePage />)} />
        <Route path="/org/:id/*" element={protect(<OrgDetailPage />)} />
        <Route path="/sites/*" element={protect(<SitesPage />)} />
        <Route path="/sites/:id" element={protect(<SiteDetailPage />)} />
        <Route path="/contracts" element={protect(<ContractsPage />)} />
        <Route path="/delegations/*" element={protect(<DelegationsPage />)} />
        <Route path="/approvals/*" element={protect(<ApprovalsPage />)} />
        <Route path="/users/*" element={protect(<UsersPage />)} />
        <Route path="/api-keys/*" element={protect(<ApiKeysPage />)} />
        <Route
          path="/external-credentials/*"
          element={protect(<ExternalCredentialsPage />)}
        />
        <Route path="/profile/*" element={protect(<ProfilePage />)} />

        {/* 온토 staff 전용 /ops */}
        <Route path="/ops/dashboard" element={opsOnly(<OpsDashboardPage />)} />
        <Route path="/ops/audit" element={opsOnly(<OpsAuditPage />)} />
        <Route
          path="/ops/organizations"
          element={opsOnly(<OpsOrganizationsPage />)}
        />

        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
}
